import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { CommentPublic, CommentTargetType } from '@cookspace-shared-contracts/core';
import { Trash2 } from 'lucide-react';

import { useDeleteCommentMutation, useListCommentsQuery } from '@/api/commentsApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';

const TARGET_TYPE_OPTIONS: Array<{ value: CommentTargetType; label: string }> = [
    { value: 'recipe', label: 'Рецепт' },
    { value: 'article', label: 'Статья' },
    { value: 'course', label: 'Курс' },
    { value: 'stream', label: 'Стрим' },
    { value: 'event', label: 'Мероприятие' }
];

const TARGET_TYPE_LABEL: Record<CommentTargetType, string> = {
    recipe: 'Рецепт',
    article: 'Статья',
    course: 'Курс',
    stream: 'Стрим',
    event: 'Мероприятие'
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

export function CommentsPage(): ReactElement {
    const [filterType, setFilterType] = useState<CommentTargetType | ''>('');
    const [filterTargetId, setFilterTargetId] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [submittedTargetId, setSubmittedTargetId] = useState('');
    const [pendingDelete, setPendingDelete] = useState<CommentPublic | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);

    const queryArg = useMemo(
        () => ({
            ...(filterType ? { targetType: filterType } : {}),
            ...(submittedTargetId ? { targetId: submittedTargetId } : {}),
            ...(submittedSearch ? { search: submittedSearch } : {}),
            limit: 50,
            offset: 0
        }),
        [filterType, submittedTargetId, submittedSearch]
    );

    const { data, isLoading, isError } = useListCommentsQuery(queryArg);

    const [deleteComment] = useDeleteCommentMutation();

    const items = data?.items ?? [];

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteComment({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch {
            setPendingDelete(null);
            setPageError('Не удалось удалить комментарий');
        }
    };

    return (
        <div data-testid="comments-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Комментарии</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="comments-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                    setSubmittedTargetId(filterTargetId.trim());
                }}
                className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="comments-filters"
            >
                <div className="w-48">
                    <Select
                        label="Тип сущности"
                        value={filterType}
                        onChange={(e) =>
                            setFilterType(e.target.value as CommentTargetType | '')
                        }
                        placeholder="Все типы"
                        options={TARGET_TYPE_OPTIONS}
                        data-testid="comments-filter-type"
                    />
                </div>
                <div className="w-72">
                    <Input
                        label="ID сущности"
                        value={filterTargetId}
                        onChange={(e) => setFilterTargetId(e.target.value)}
                        placeholder="UUID"
                        data-testid="comments-filter-target-id"
                    />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Input
                        label="Поиск по тексту"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        data-testid="comments-search-input"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="comments-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Применить
                </button>
            </form>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="comments-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="comments-error">
                    Не удалось загрузить комментарии
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="comments-table"
                >
                    <div className="grid grid-cols-[80px_100px_minmax(0,1fr)_minmax(0,2fr)_60px_140px_60px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Тип</span>
                        <span>Автор</span>
                        <span>Текст</span>
                        <span className="text-right">Рейтинг</span>
                        <span>Создан</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Комментарии не найдены
                        </div>
                    )}

                    {items.map((comment) => (
                        <div
                            key={comment.id}
                            className="grid grid-cols-[80px_100px_minmax(0,1fr)_minmax(0,2fr)_60px_140px_60px] items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`comment-row-${comment.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {comment.id.slice(0, 8)}
                            </span>
                            <span>
                                <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                    {TARGET_TYPE_LABEL[comment.targetType]}
                                </span>
                            </span>
                            <span className="truncate text-gray-900">
                                {comment.authorDisplayName}
                            </span>
                            <span className="line-clamp-3 text-gray-700">{comment.text}</span>
                            <span className="text-right text-gray-700">
                                {comment.rating ?? '—'}
                            </span>
                            <span className="text-xs text-gray-600">
                                {formatDate(comment.createdAt)}
                            </span>
                            <span className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setPendingDelete(comment)}
                                    data-testid={`comment-delete-${comment.id}`}
                                    className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                    aria-label="Удалить"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Удалить комментарий"
                message={
                    pendingDelete
                        ? `Удалить комментарий от «${pendingDelete.authorDisplayName}»? Действие необратимо.`
                        : ''
                }
                confirmLabel="Удалить"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
